import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';

import { Container } from 'aurelia-framework';
import { FORM_SUBTYPE } from '~/app-routes';
import { NavigationState } from '~/components/navigation/navigation-state';
import {
	NavigationBookmark,
	NavigationBookmarkRouteParams,
	NavigationMenuItem,
	NavigationViewStructure,
} from '~/components/navigation/navigation-types';
import { CommonURLParam, TREE_VIEW_SESSION_VARIABLE_NAME } from '~/constants';
import { EntityType } from '~/models/entities/entity-type';
import { NavigationEntity } from './navigation-types';

export const RoutePathRoot = '/#/';

export function computeNavigationName(item: NavigationMenuItem): string {
	if (
		item.name &&
		// NOTE: Jake: 2019-10-03 - WEBUI-2494
		// A confusing bit of behaviour occurs here.
		// It seems like the backend is automatically populating "name" with
		// "formName" if that field is empty, but only for the "menuItemList", not
		// the "quickMenuList".
		item.name !== item.formName
	) {
		// NOTE: Jake: 2019-11-01
		// Use "name" configured by user/client.
		return item.name;
	}
	if (!item.formName) {
		return '';
	}
	// Fallback to translation
	const parts = item.formName.split('/');
	const translationKey = 'route-title.' + parts[0];
	let result: string | undefined = '';
	if (parts.length === 1) {
		result = Container.instance.get(I18N).tr(translationKey);
	} else {
		// Check if we have a specific translation key for the subtype (for example, "route-title.address-details-SM").
		// If not, use the generic with the subtype as the option parameter.
		const translationKeySubtype = translationKey + '-' + parts[1];
		if (
			Container.instance.get(I18N).i18next.exists(translationKeySubtype)
		) {
			result = Container.instance.get(I18N).tr(translationKeySubtype);
		} else {
			result = Container.instance.get(I18N).tr(translationKey, {
				[FORM_SUBTYPE]: parts[1],
			});
		}
	}
	if (!result) {
		return '';
	}
	return result.trim();
}

export function computeNavURL(
	item: NavigationMenuItem | undefined,
	routeParams: string | { [param: string]: string } | URLSearchParams,
): string {
	if (!item || !item.formName) {
		// If there is no item or the item's formName is not set or missing, return empty string as it's invalid.
		return '';
	}
	const router = Container.instance.get(Router);
	const parts = item.formName.split('/');
	if (!router.hasRoute(parts[0])) {
		// NOTE: Jake: 2019-11-01 - WEBUI-2578
		// If this route doesn't exist, return empty string
		// as it's invalid.
		// We use this behaviour in navigation-state to print a list
		// of routes that are invalid in the console. (DEVELOPMENT mode only)
		return '';
	}

	// Handle route params
	const params = makeURLSearchParams(routeParams);
	if (item.customisationName) {
		params.set(CommonURLParam.CUSTOMISATION, item.customisationName);
	}
	if (item.filters) {
		params.set('filters', item.filters);
	}
	if (item.useArchive) {
		params.set(CommonURLParam.USE_ARCHIVE, 'true');
	}

	// Add parameters if any are set
	let urlPart = '';
	const paramsStr = params.toString();
	if (paramsStr !== '') {
		urlPart = '?' + paramsStr;
	}

	return RoutePathRoot + item.formName + urlPart;
}

function makeURLSearchParams(
	routeParams: string | { [param: string]: string } | URLSearchParams,
): URLSearchParams {
	let params: URLSearchParams;
	if (routeParams instanceof URLSearchParams) {
		params = new URLSearchParams(routeParams);
	} else {
		switch (typeof routeParams) {
			case 'string': {
				const routeParamParts = routeParams.split('?');
				params = new URLSearchParams(routeParamParts[1]);
				break;
			}
			case 'object': {
				params = new URLSearchParams();
				for (const param in routeParams) {
					if (!routeParams.hasOwnProperty(param)) {
						continue;
					}
					params.set(param, routeParams[param]);
				}
				break;
			}
		}
	}
	return params;
}

/**
 * generateBookmarkFromRouter if various key elements are set.
 */
export function generateBookmarkFromRouter(
	router: Router,
): NavigationBookmark | undefined {
	if (
		!router ||
		!router.currentInstruction.config ||
		!router.currentInstruction.config.name ||
		!router.currentInstruction.config.route
	) {
		throw new Error('Missing information from route.');
	}

	// Get route
	let route: string = '';
	{
		const routes = router.currentInstruction.config.route;
		if (typeof routes === 'string') {
			route = routes;
		} else {
			route = routes[0];
		}
	}

	//
	let currentView =
		router.currentInstruction.queryParams[CommonURLParam.NAV_VIEW];
	if (!currentView) {
		currentView = '';
	}

	// NOTE(Jae): 2020-02-21
	// entityType used to be the unique key for menu information. To keep bookmarks
	// mostly the same, it just uses NAV_ID instead now.
	// (Which is equivalent to entityType if the user doesn't add an "id" property to their navigation JSON)
	let searchEntityType =
		router.currentInstruction.queryParams[CommonURLParam.NAV_ID];
	if (!searchEntityType) {
		searchEntityType = '';
	}

	return {
		currentView,
		formName: router.currentInstruction.config.name,
		route,
		routeParams: getRouteParamsFromCurrentURL(),
		searchEntityType,
	};
}

export function computeFirstRouteInStructure(
	navigationEntity: NavigationEntity,
): string {
	for (const navigationMenuItem of navigationEntity.menuItemList) {
		if (navigationMenuItem.formName != null) {
			return navigationMenuItem.formName;
		}

		const computedFormName = computeFirstRouteInNavMenu(navigationMenuItem);
		if (computedFormName != null) {
			return computedFormName;
		}
	}
	return null;
}

function computeFirstRouteInNavMenu(
	parentMenuItem: NavigationMenuItem,
): string {
	for (const navigationMenuItem of parentMenuItem.childMenuItems) {
		if (navigationMenuItem.formName != null) {
			return navigationMenuItem.formName;
		}

		const computedFormName = computeFirstRouteInNavMenu(navigationMenuItem);
		if (computedFormName != null) {
			return computedFormName;
		}
	}
	return null;
}

function getRouteParamsFromCurrentURL(): NavigationBookmarkRouteParams {
	const routeParams: NavigationBookmarkRouteParams = {};
	const urlParams = new URLSearchParams(window.location.hash.split('?')[1]);
	const customisationName: string | null = urlParams.get(
		CommonURLParam.CUSTOMISATION,
	);
	if (customisationName) {
		routeParams.customisationName = customisationName;
	}
	if (Object.keys(routeParams).length === 0) {
		return {};
	}
	return routeParams;
}

export function isBookmarkEqual(
	a: NavigationBookmark,
	b: NavigationBookmark,
): boolean {
	// NOTE: Jake: 2019-05-06 - WEBUI-1571
	// We do NOT use lodash-es/isEqual here because it will return false if observers
	// are applied to properties. (Which they are for aurelia-store values!)
	const aRouteParams = a.routeParams ? a.routeParams : {};
	const bRouteParams = b.routeParams ? b.routeParams : {};
	if (
		a.currentView === b.currentView &&
		a.formName === b.formName &&
		a.route === b.route &&
		a.searchEntityType === b.searchEntityType &&
		aRouteParams.customisationName === bRouteParams.customisationName
	) {
		return true;
	}
	return false;
}

const commonNavigationParameters: string[] = [
	CommonURLParam.DIVISION,
	CommonURLParam.NAV_ID,
	CommonURLParam.NAV_VIEW,
	CommonURLParam.NAV_HIDE,
];

const navigationParameters = new Map<string, string[]>([
	[
		EntityType.MEMBER,
		[CommonURLParam.FUND, CommonURLParam.MEMBER, CommonURLParam.CLIENT],
	],
	[EntityType.EMPLOYER, [CommonURLParam.FUND, CommonURLParam.EMPLOYER]],
	[EntityType.ADVISER, [CommonURLParam.ADVISER]],
	[EntityType.INVESTMENT_MANAGER, [CommonURLParam.INVESTMENT_MANAGER]],
]);

export function filterStatefulQueryParameters(
	currentParams: { [param: string]: string },
	paramsToDelete?: string[],
): { [param: string]: string } {
	// NOTE: Jae: 2020-01-23 - WEBUI-2856
	// Passing in a JS object (currentParams) to "new URLSearchParams" is undefined behaviour
	// and caused TreeView to not generate the URL correctly.
	// So we manually fill out the URLSearchParams() object now.
	const currentURLQueryParams = new URLSearchParams();
	for (const param in currentParams) {
		if (!currentParams.hasOwnProperty(param)) {
			continue;
		}
		currentURLQueryParams.set(param, currentParams[param]);
	}

	const statefulURLSearchParams = filterStatefulURLQueryParams(
		currentURLQueryParams,
		paramsToDelete,
	);

	const statefulParamsObject: { [param: string]: string } = {};
	for (const param of statefulURLSearchParams.keys()) {
		statefulParamsObject[param] = statefulURLSearchParams.get(param);
	}
	return statefulParamsObject;
}

export function filterStatefulURLQueryParams(
	currentParams: URLSearchParams,
	paramsToDelete: string[] = [],
): URLSearchParams {
	// Get nav entity params
	let navEntityParams: string[] | undefined;
	{
		const navState = Container.instance.get(NavigationState);
		const navId = currentParams.get(CommonURLParam.NAV_ID);
		const navView = currentParams.get(CommonURLParam.NAV_VIEW);
		const entity = navState.getEntityFromID(navId, navView);
		// NOTE(Albert): 2020-07-13 - WEBUI-3460
		// For tree view, we cannot filter out parameters based on `entityType` declared in navigation.json
		// because URL parameters use the first `nav-view` (alphabetically) and the first `entityType` declared
		// in the `entityList` even if form is declared in a different `nav-view` under a different `entityType`
		// E.g. A navigation file with entityType `EMPTY` will filter out all entity parameters from URL
		// So for tree view, include all entity parameters in URL. If parameters need to be removed from
		// the URL in tree view, use function parameter `paramsToDelete` instead
		if (sessionStorage.getItem(TREE_VIEW_SESSION_VARIABLE_NAME) === '1') {
			navEntityParams = [];
			navigationParameters.forEach((entityParams, _) => {
				navEntityParams.push(
					...entityParams.filter(
						(param) => navEntityParams.indexOf(param) < 0,
					),
				);
			});
		} else if (entity !== undefined) {
			navEntityParams = navigationParameters.get(entity.entityType);
		} else {
			navEntityParams = navigationParameters.get('MEMBER');
		}
	}

	const filteredParams = new URLSearchParams();
	for (const param of currentParams.keys()) {
		const commonParamsToKeep = commonNavigationParameters.filter(
			(commonParam) => paramsToDelete.indexOf(commonParam) < 0,
		);
		if (commonParamsToKeep.some((commonParam) => param === commonParam)) {
			filteredParams.set(param, currentParams.get(param));
		}

		if (navEntityParams && navEntityParams.length > 0) {
			const navParamsToKeep = navEntityParams.filter(
				(commonParam) => paramsToDelete.indexOf(commonParam) < 0,
			);
			if (
				navParamsToKeep.some(
					(navEntityParam) => param === navEntityParam,
				)
			) {
				filteredParams.set(param, currentParams.get(param));
			}
		}
	}
	return filteredParams;
}

/**
 * Get flattened NavigationMenuItems of the given view and viewId
 * @param view NavigationViewStructure
 * @returns flattened NavigationMenuItems
 */
export function getNavigationMenuItemsOfView(view: NavigationViewStructure) {
	const matchingMenuItem = view.structure.entityList.flatMap((entity) =>
		entity.menuItemList.concat(entity.quickMenuList),
	);
	return getMenuItemWithChildren(matchingMenuItem);
}

function getMenuItemWithChildren(menuItems: NavigationMenuItem[]) {
	const itemsWithChildren = menuItems.filter(
		(item) => item.childMenuItems.length > 0,
	);
	if (itemsWithChildren.length === 0) {
		return menuItems;
	}
	return getMenuItemWithChildren([
		...menuItems.filter((item) => item.childMenuItems.length === 0),
		...itemsWithChildren.flatMap((item) => item.childMenuItems),
	]);
}
